import { labelsMatch, normalizeLabel, stripTableQualifier } from './labelMatching'
import type { DashboardWidget } from './types'
import { parseDashboardDate } from './vizUtils'

type SortableColumn = {
  label: string
  sortKey: string
}

export function resolveWidgetSortKey(widget: DashboardWidget, requestedColumn: string | null | undefined) {
  if (!requestedColumn) return null

  const dimensionMatch = findDimensionOption(widget, requestedColumn)
  if (dimensionMatch?.value) {
    return dimensionMatch.value
  }

  const measureMatch = (widget.measures ?? []).find((measure) => (
    labelsMatch(requestedColumn, measure?.value)
    || labelsMatch(requestedColumn, measure?.name)
    || labelsMatch(requestedColumn, stripTableQualifier(measure?.value))
  ))
  if (measureMatch?.value) {
    return measureMatch.value
  }

  return requestedColumn
}

export function buildWidgetSortOptions(widget: DashboardWidget, columns: string[]): SortableColumn[] {
  const seen = new Set<string>()

  return columns.flatMap((column) => {
    const sortKey = resolveWidgetSortKey(widget, column)
    if (!sortKey || seen.has(sortKey)) return []
    seen.add(sortKey)
    return [{ label: column, sortKey }]
  })
}

function looksLikeDateDimension(value: string | null | undefined) {
  const normalized = normalizeLabel(value)
  return normalized.includes('date')
    || normalized.includes('month')
    || normalized.includes('week')
    || normalized.includes('year')
    || normalized.includes('period')
}

function findDimensionOption(widget: DashboardWidget, field: string | null | undefined) {
  return (widget.dimensions ?? []).find((dimension) => (
    labelsMatch(field, dimension?.value)
    || labelsMatch(field, dimension?.name)
    || labelsMatch(stripTableQualifier(field), stripTableQualifier(dimension?.value))
  )) ?? null
}

/** The dimension's field ref when it is a date or a calendar label (date type, or a name like "Month Year"). */
export function resolveDateDimensionKey(widget: DashboardWidget, field: string | null | undefined) {
  if (!field) return null
  const option = findDimensionOption(widget, field)
  const isDate = looksLikeDateDimension(option?.type)
    || looksLikeDateDimension(option?.name)
    || looksLikeDateDimension(option?.value)
    || looksLikeDateDimension(field)
  return isDate ? option?.value ?? field : null
}

/** The widget's row (group-by) dimension before any runtime choice. */
function resolveRowDimension(widget: DashboardWidget) {
  return widget.rowDimension
    ?? widget.daxGenerationRequest?.dimensions?.[0]
    ?? widget.dimensions?.[0]?.value
    ?? null
}

/**
 * Default sort column: a widget whose rows are dates or calendar months sorts by them, oldest first
 * (sent with sortDirection 'asc'). A line always sorts by its date dimension, falling back to its
 * first dimension. Other widgets have no default sort (null).
 */
export function resolveDefaultDateSortKey(widget: DashboardWidget, groupByDimension?: string | null) {
  if (widget.type !== 'line') return resolveDateDimensionKey(widget, groupByDimension ?? resolveRowDimension(widget))

  const dimensions = widget.dimensions ?? []
  const preferredDimension = dimensions.find((dimension) => (
    looksLikeDateDimension(dimension.type)
    || looksLikeDateDimension(dimension.name)
    || looksLikeDateDimension(dimension.value)
  ))

  return preferredDimension?.value
    ?? widget.rowDimension
    ?? widget.daxGenerationRequest?.dimensions?.[0]
    ?? dimensions[0]?.value
    ?? null
}

/** True when `column` is one of the widget's dimensions rather than a measure. */
export function isDimensionSortKey(widget: DashboardWidget, column: string | null | undefined) {
  return Boolean(column && findDimensionOption(widget, column))
}

const MONTH_NAMES = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
const MONTH_YEAR_PATTERN = /^([a-z]{3,9})\.?[\s/-]+(\d{4})$/i
const YEAR_MONTH_PATTERN = /^(\d{4})-(\d{1,2})$/
const ISO_OR_SLASH_DATE_PATTERN = /^(\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}\/\d{2,4})/

/**
 * A calendar value's time for sorting: ISO and slash dates, "Sep 2025" / "September 2025" and "2025-09".
 * Anything else (including numbers, which the server already orders) is null, so free text is never
 * mistaken for a date.
 */
export function parseCalendarSortValue(value: unknown): number | null {
  if (typeof value !== 'string') return null
  const text = value.trim()
  if (ISO_OR_SLASH_DATE_PATTERN.test(text)) return parseDashboardDate(text)?.getTime() ?? null
  const monthYear = MONTH_YEAR_PATTERN.exec(text)
  if (monthYear) {
    const month = MONTH_NAMES.indexOf(String(monthYear[1]).slice(0, 3).toLowerCase())
    return month >= 0 ? new Date(Number(monthYear[2]), month, 1).getTime() : null
  }
  const yearMonth = YEAR_MONTH_PATTERN.exec(text)
  if (yearMonth) {
    const month = Number(yearMonth[2])
    return month >= 1 && month <= 12 ? new Date(Number(yearMonth[1]), month - 1, 1).getTime() : null
  }
  return null
}

/**
 * Orders items by a calendar value when every non-empty value parses (see parseCalendarSortValue);
 * otherwise returns them unchanged. Ties keep their order. A text month column sorted on the server
 * comes back alphabetical ("Apr", "Aug", ...), so this puts it back in calendar order.
 */
export function sortByCalendarValue<T>(items: T[], valueOf: (item: T) => unknown, direction: 'asc' | 'desc' = 'asc'): T[] {
  if (items.length < 2) return items
  const keyed = items.map((item, index) => ({ item, index, raw: valueOf(item), time: parseCalendarSortValue(valueOf(item)) }))
  const present = keyed.filter((entry) => entry.raw !== null && entry.raw !== undefined && entry.raw !== '')
  if (present.length < 2 || present.some((entry) => entry.time === null)) return items
  const sign = direction === 'desc' ? -1 : 1
  return [...keyed]
    .sort((left, right) => {
      // Empty values go last either way.
      if (left.time === null || right.time === null) return left.time === null ? (right.time === null ? left.index - right.index : 1) : -1
      return (left.time - right.time) * sign || left.index - right.index
    })
    .map((entry) => entry.item)
}
